/**
 * Authentication Store (Zustand)
 *
 * Manages user authentication state, login/logout, and token management.
 * Supports two-step MFA login flow.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { config } from '../config/runtime';
import { queryClient } from '../lib/queryClient';

export interface User {
  user_id: string;
  customer_id: string;
  /**
   * Display name of the user's customer tenant (config.customers.name).
   * Drives the customer-centric dashboard label for non-tenant-admin users.
   * UI-only — does not gate access control. Typed optional so transitional
   * new-FE-old-BE deploys gracefully fall through to the literal portfolio name.
   */
  customer_name?: string | null;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  permissions: Record<string, any>;
  /**
   * First-granted organization-typed group membership, if any. Retained as a
   * forward-compat primitive for sub-customer scoping; not used by the
   * dashboard label today (label derives from customer_name above).
   */
  organization?: { org_unit_id: string; name: string } | null;
}

interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;

  // MFA state (ephemeral — not persisted)
  mfaPending: boolean;
  mfaSessionToken: string | null;

  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  clearMfaState: () => void;
  logout: () => void;
  setUser: (user: User, token: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      mfaPending: false,
      mfaSessionToken: null,

      login: async (email: string, password: string) => {
        const response = await fetch(`${config.API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Login failed');
        }

        const data = await response.json();

        if (data.mfa_required) {
          // MFA step required — store session token ephemerally
          set({
            mfaPending: true,
            mfaSessionToken: data.session_token,
            user: null,
            token: null,
            isAuthenticated: false
          });
          return;
        }

        // No MFA — complete login. Clear any cached queries left over from a
        // prior session in this tab so one user's data can't bleed into the
        // next (e.g. a prior admin's security dashboard / banner).
        queryClient.clear();
        set({
          user: data.user,
          token: data.token,
          isAuthenticated: true,
          mfaPending: false,
          mfaSessionToken: null
        });
      },

      verifyMfa: async (code: string) => {
        const { mfaSessionToken } = get();
        if (!mfaSessionToken) {
          throw new Error('No MFA session active');
        }

        const response = await fetch(`${config.API_URL}/auth/verify-mfa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_token: mfaSessionToken, code })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Invalid verification code');
        }

        const data = await response.json();
        // New session established — drop any cached queries from a prior user.
        queryClient.clear();
        set({
          user: data.user,
          token: data.token,
          isAuthenticated: true,
          mfaPending: false,
          mfaSessionToken: null
        });
      },

      clearMfaState: () => {
        set({ mfaPending: false, mfaSessionToken: null });
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, mfaPending: false, mfaSessionToken: null });
        // Purge cached queries so the next user in this tab starts clean.
        queryClient.clear();
      },

      setUser: (user: User, token: string) => {
        set({ user, token, isAuthenticated: true });
      },

      clearAuth: () => {
        set({ user: null, token: null, isAuthenticated: false, mfaPending: false, mfaSessionToken: null });
        // Purge cached queries so the next user in this tab starts clean.
        queryClient.clear();
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
        // mfaPending and mfaSessionToken are NOT persisted
      })
    }
  )
);
